import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Plus, Search, Users, X } from 'lucide-react';
import {
  useDeleteTeamMemberMutation,
  useTeamMembersQuery,
} from '../api/useTeamQueries';
import { TeamMemberCard } from '../components/TeamMemberCard';
import { normalizeTeamMemberList } from '../utils/teamUtils';

export default function TeamPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const listQ = useTeamMembersQuery({ page, limit: 12 });
  const deleteMut = useDeleteTeamMemberMutation();

  const members = useMemo(() => normalizeTeamMemberList(listQ.data), [listQ.data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) =>
      [m.firstName, m.lastName, m.email, m.mobileNumber]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [members, search]);

  const pagination = (listQ.data as { data?: { pagination?: Record<string, number> } })
    ?.data?.pagination;
  const totalPages = pagination?.totalPages || pagination?.pages || 1;
  const total = pagination?.totalCount || pagination?.total || members.length;
  const isPending = listQ.isPending;

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-5">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="text-left">
          <h1 className="m-0 mt-1 text-2xl font-semibold tracking-tight text-[var(--dash-ink)]">
            Your team
          </h1>
          <p className="mt-1 mb-0 text-sm text-[var(--dash-muted)]">
            {isPending
              ? 'Loading team members…'
              : `${total} member${total === 1 ? '' : 's'} · manage sub-accounts & client access`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/team/add')}
          className="dash-btn-gradient inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition"
        >
          <Plus className="h-4 w-4" />
          Add Team Member
        </button>
      </header>

      <div className="camp-fade-up flex flex-col gap-3 rounded-2xl border border-[var(--dash-line)] bg-white p-4 shadow-[0_10px_28px_-22px_rgba(16,38,73,0.35)] sm:flex-row sm:items-center">
        <div className="relative flex-1 md:max-w-sm">
          <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-[var(--dash-muted)]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, phone"
            className="w-full rounded-xl border border-[var(--dash-line)] bg-[var(--dash-soft)] py-2.5 pr-10 pl-10 text-sm outline-none transition focus:border-[var(--dash-brand)] focus:bg-white focus:ring-2 focus:ring-[var(--dash-brand)]/15"
          />
          {search ? (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute top-1/2 right-2.5 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-[rgba(15,27,45,0.08)] text-[var(--dash-muted)]"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" strokeWidth={2.5} />
            </button>
          ) : null}
        </div>
      </div>

      {isPending ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="camp-skeleton h-[220px] rounded-2xl border border-[var(--dash-line)]" />
          ))}
        </div>
      ) : members.length === 0 ? (
        <div className="flex min-h-[42vh] flex-col items-center justify-center rounded-3xl border border-dashed border-[var(--dash-line)] bg-white px-6 py-12 text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--dash-brand-soft)] text-[var(--dash-brand)]">
            <Users className="h-7 w-7" />
          </div>
          <h2 className="m-0 text-lg font-semibold text-[var(--dash-ink)]">No team members yet</h2>
          <p className="mt-2 mb-6 max-w-sm text-sm text-[var(--dash-muted)]">
            Invite your first sub-account and assign what they can access.
          </p>
          <button
            type="button"
            onClick={() => navigate('/team/add')}
            className="dash-btn-gradient inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white"
          >
            <Plus className="h-4 w-4" />
            Add Team Member
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex min-h-[28vh] flex-col items-center justify-center rounded-3xl border border-dashed border-[var(--dash-line)] bg-white px-6 py-10 text-center">
          <h2 className="m-0 text-base font-semibold text-[var(--dash-ink)]">No matching team members</h2>
          <p className="mt-2 mb-0 text-sm text-[var(--dash-muted)]">Try a different search.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((member, i) => (
            <TeamMemberCard
              key={member._id}
              member={member}
              index={i}
              onView={(id) => navigate(`/team/${id}`)}
              onEdit={(id) => navigate(`/team/${id}/edit`)}
              onDelete={setDeleteId}
            />
          ))}
        </div>
      )}

      {!isPending && members.length > 0 && filtered.length > 0 && totalPages > 1 && !search.trim() ? (
        <div className="flex items-center justify-between rounded-2xl border border-[var(--dash-line)] bg-white px-4 py-3">
          <span className="text-xs text-[var(--dash-muted)]">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="rounded-xl border border-[var(--dash-line)] px-3 py-1.5 text-xs font-semibold text-[var(--dash-ink)] disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-xl border border-[var(--dash-line)] px-3 py-1.5 text-xs font-semibold text-[var(--dash-ink)] disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      ) : null}

      {deleteId ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close"
            onClick={() => setDeleteId(null)}
          />
          <div className="camp-fade-up relative z-10 w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="m-0 text-lg font-semibold text-[var(--dash-ink)]">Remove this team member?</h2>
            <p className="mt-2 mb-0 text-sm text-[var(--dash-muted)]">
              This permanently revokes their access to your agency account. This cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteId(null)}
                className="h-10 rounded-xl px-4 text-sm font-medium text-[var(--dash-muted)] hover:bg-[var(--dash-soft)]"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleteMut.isPending}
                onClick={() =>
                  deleteId &&
                  deleteMut.mutate(deleteId, { onSuccess: () => setDeleteId(null) })
                }
                className="inline-flex h-10 items-center gap-2 rounded-xl bg-red-600 px-4 text-sm font-semibold text-white disabled:opacity-50"
              >
                {deleteMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Remove
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
